/**
 * Created by neo on 10.01.2026
 * Challenge Stats Component - Shows key statistics about the challenge
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { RouteChallenge } from '../../../Model/Engagement/RouteChallenge/RouteChallenge';
import { IndividualChallenge } from '../../../Model/Engagement/IndividualChallenge/IndividualChallenge';
import { Theme } from '../../../Theme/Theme';
import { ChallengeType } from '../ChallengeDetailScreen';

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 32px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: center;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
`;

const StatIcon = styled.div`
  font-size: 28px;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: ${Theme.Colors.charcoal};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export type ChallengeStatsProps = {
  challenge: RouteChallenge | IndividualChallenge;
  challengeType: ChallengeType;
  teamsCount?: number;
  participantsCount?: number;
};

export const ChallengeStats: React.FC<ChallengeStatsProps> = observer(
  ({ challenge, challengeType, teamsCount = 0, participantsCount = 0 }) => {
    const { t } = useTrans();

    const isIndividual = 'challengeType' in challenge && challenge.challengeType === 'individual';
    const totalDays = dayjs(challenge.endDateTime).diff(dayjs(challenge.startDateTime), 'day');
    const daysRemaining = isIndividual
      ? challenge.daysRemaining
      : Math.max(0, dayjs(challenge.endDateTime).diff(dayjs(), 'day'));
    const totalLocations = challenge.locations.length;
    const totalPoints = challenge.totalCollectedPoints;

    return (
      <StatsContainer>
        <StatCard>
          <StatIcon>📍</StatIcon>
          <StatValue>{totalLocations}</StatValue>
          <StatLabel>{t('challenge.stats.locations', { defaultValue: 'Locations' })}</StatLabel>
        </StatCard>

        <StatCard>
          <StatIcon>⏱️</StatIcon>
          <StatValue>
            {challenge.ended ? t('challenge.stats.ended', { defaultValue: 'Ended' }) : `${daysRemaining}`}
          </StatValue>
          <StatLabel>
            {challenge.ended
              ? ''
              : t('challenge.stats.daysLeft', { defaultValue: 'Days Left' })}
          </StatLabel>
        </StatCard>

        <StatCard>
          <StatIcon>{challengeType === 'individual' ? '👤' : '👥'}</StatIcon>
          <StatValue>{challengeType === 'individual' ? participantsCount : teamsCount}</StatValue>
          <StatLabel>
            {challengeType === 'individual'
              ? t('challenge.stats.participants', { defaultValue: 'Participants' })
              : t('challenge.stats.teams', { defaultValue: 'Teams' })}
          </StatLabel>
        </StatCard>

        <StatCard>
          <StatIcon>⭐</StatIcon>
          <StatValue>{totalPoints.toLocaleString()}</StatValue>
          <StatLabel>{t('challenge.stats.totalPoints', { defaultValue: 'Total Points' })}</StatLabel>
        </StatCard>
      </StatsContainer>
    );
  },
);
